/**
 * Kleda Client App - Analytics
 * Track user interactions for conversion optimization
 *
 * Integrates with:
 * - Vercel Analytics (automatic page views)
 * - Google Tag Manager (custom events)
 */

// =============================================================================
// TYPES
// =============================================================================

type EventName =
  | 'voorinspectie_viewed'
  | 'rand_reparatie_viewed'
  | 'rand_reparatie_confirmed'
  | 'rand_reparatie_declined'
  | 'ondertapijt_viewed'
  | 'ondertapijt_confirmed'
  | 'ondertapijt_declined'
  | 'vlekkenbaas_viewed'
  | 'vlekkenbaas_confirmed'
  | 'vlekkenbaas_declined'
  | 'upsell_viewed'
  | 'upsell_confirmed'
  | 'upsell_declined'
  | 'review_clicked'
  | 'crosssell_clicked'
  | 'payment_initiated'
  | 'timeline_step_viewed'
  | 'photo_clicked'
  | 'info_expanded'
  | 'vloerkleed_edit_opened'
  | 'vloerkleed_edited'
  | 'vloerkleed_deleted'
  | 'fotos_geopend'
  | 'terugkom_viewed'
  | 'terugkom_confirmed'
  | 'terugkom_declined'
  | 'abonnement_viewed'
  | 'abonnement_confirmed'
  | 'mijn_kleda_viewed'
  | 'bestel_opnieuw_clicked'
  | 'mailvoorkeur_changed'
  | 'mijn_kleda_inlog_aangevraagd'
  | 'mijn_kleda_ingelogd'
  | 'mijn_kleda_uitgelogd'
  | 'gegevens_gewijzigd'
  | 'email_bevestigd'

interface EventProperties {
  // Order context
  werkOrderId?: string
  werkOrderNummer?: string
  // Terugkom-moment / abonnement
  seizoen?: string
  kanaal?: string
  optieCount?: number
  intervalMaanden?: number
  /** Waar de klant vandaan kwam (s10-chip, care-guide, mijn-kleda, ...). */
  bron?: string
  aan?: boolean
  /** Mijn Kleda: basis (ordertoken) of volledig (ingelogd). */
  niveau?: 'basis' | 'volledig'
  /** Mijn Kleda gegevens: welk veld, en bij adres de reikwijdte (order of klantkaart). */
  veld?: 'adres' | 'telefoon' | 'email'
  scope?: string
  // Voorinspectie
  totalM2?: number
  itemCount?: number
  // Upsell specific
  totalPrice?: number
  discount?: number
  selectedItemIds?: string[]
  upsellType?: 'ondertapijt' | 'rand_reparatie' | 'vlekkenbaas'
  // Review & cross-sell
  reviewPlatform?: 'google' | 'klantenvertellen'
  crosssellType?: string
  // Payment
  amount?: number
  // Timeline
  timelineStep?: string
  // Photo/info
  itemId?: string
  itemName?: string
  infoType?: 'diep_vuil' | 'looppad' | 'vlekken'
  // Foto-ingang: welke plaatsing de klik opleverde ('direct' = één kleed, dus
  // meteen doorgestuurd naar de timeline zonder tussenscherm)
  fotosVariant?: 'direct' | 'kiezer' | 'leeg'
}

// =============================================================================
// GTM DATAAYER
// =============================================================================

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>
  }
}

/**
 * Push event to GTM dataLayer
 */
function pushToDataLayer(eventName: string, properties: EventProperties) {
  if (typeof window === 'undefined') return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    event: eventName,
    ...properties,
    timestamp: new Date().toISOString()
  })
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Track a user interaction event
 *
 * @example
 * trackEvent('voorinspectie_viewed', { werkOrderId: '123', totalM2: 15.5, itemCount: 2 })
 * trackEvent('rand_reparatie_confirmed', { totalPrice: 75.50, selectedItemIds: ['a0X...'] })
 */
export function trackEvent(name: EventName, properties: EventProperties = {}) {
  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] ${name}`, properties)
  }

  // Push to GTM
  pushToDataLayer(`kleda_${name}`, properties)
}

/**
 * Track voorinspectie page view
 */
export function trackVoorinspectieViewed(
  werkOrderId: string,
  werkOrderNummer: string,
  totalM2: number,
  itemCount: number
) {
  trackEvent('voorinspectie_viewed', {
    werkOrderId,
    werkOrderNummer,
    totalM2,
    itemCount
  })
}

/**
 * Track rand reparatie card view
 */
export function trackRandReparatieViewed(
  werkOrderId: string,
  totalPrice: number,
  itemCount: number
) {
  trackEvent('rand_reparatie_viewed', {
    werkOrderId,
    totalPrice,
    itemCount
  })
}

/**
 * Track rand reparatie confirmation
 */
export function trackRandReparatieConfirmed(
  werkOrderId: string,
  totalPrice: number,
  selectedItemIds: string[]
) {
  trackEvent('rand_reparatie_confirmed', {
    werkOrderId,
    totalPrice,
    itemCount: selectedItemIds.length,
    selectedItemIds
  })
}

/**
 * Track ondertapijt card view
 */
export function trackOndertapijtViewed(
  werkOrderId: string,
  totalPrice: number,
  discount: number,
  itemCount: number
) {
  trackEvent('ondertapijt_viewed', {
    werkOrderId,
    totalPrice,
    discount,
    itemCount
  })
}

/**
 * Track ondertapijt confirmation
 */
export function trackOndertapijtConfirmed(
  werkOrderId: string,
  totalPrice: number,
  discount: number,
  selectedItemIds: string[]
) {
  trackEvent('ondertapijt_confirmed', {
    werkOrderId,
    totalPrice,
    discount,
    itemCount: selectedItemIds.length,
    selectedItemIds
  })
}

/**
 * Track photo link click
 */
export function trackPhotoClicked(itemId: string, itemName: string) {
  trackEvent('photo_clicked', { itemId, itemName })
}

/**
 * Track info section expanded
 */
export function trackInfoExpanded(infoType: 'diep_vuil' | 'looppad' | 'vlekken') {
  trackEvent('info_expanded', { infoType })
}

// =============================================================================
// VLEKKENBAAS TRACKING
// =============================================================================

/**
 * Track vlekkenbaas upsell card view
 */
export function trackVlekkenbaasViewed(werkOrderId: string, price: number) {
  trackEvent('vlekkenbaas_viewed', { werkOrderId, totalPrice: price })
}

/**
 * Track vlekkenbaas confirmation
 */
export function trackVlekkenbaasConfirmed(werkOrderId: string, price: number) {
  trackEvent('vlekkenbaas_confirmed', { werkOrderId, totalPrice: price })
}

/**
 * Track vlekkenbaas declined
 */
export function trackVlekkenbaasDeclined(werkOrderId: string) {
  trackEvent('vlekkenbaas_declined', { werkOrderId })
}

// =============================================================================
// GENERIC UPSELL TRACKING
// =============================================================================

/**
 * Track generic upsell view (for UpsellCard component)
 */
export function trackUpsellViewed(
  upsellType: 'ondertapijt' | 'rand_reparatie' | 'vlekkenbaas',
  werkOrderId: string,
  totalPrice: number
) {
  trackEvent('upsell_viewed', { upsellType, werkOrderId, totalPrice })
}

/**
 * Track generic upsell confirmation
 */
export function trackUpsellConfirmed(
  upsellType: 'ondertapijt' | 'rand_reparatie' | 'vlekkenbaas',
  werkOrderId: string,
  totalPrice: number
) {
  trackEvent('upsell_confirmed', { upsellType, werkOrderId, totalPrice })
}

/**
 * Track generic upsell declined
 */
export function trackUpsellDeclined(
  upsellType: 'ondertapijt' | 'rand_reparatie' | 'vlekkenbaas',
  werkOrderId: string
) {
  trackEvent('upsell_declined', { upsellType, werkOrderId })
}

// =============================================================================
// REVIEW & CROSS-SELL TRACKING
// =============================================================================

/**
 * Track review link clicked
 */
export function trackReviewClicked(
  werkOrderId: string,
  platform: 'google' | 'klantenvertellen'
) {
  trackEvent('review_clicked', { werkOrderId, reviewPlatform: platform })
}

/**
 * Track cross-sell link clicked
 */
export function trackCrosssellClicked(
  werkOrderId: string,
  crosssellType: string
) {
  trackEvent('crosssell_clicked', { werkOrderId, crosssellType })
}

// =============================================================================
// PAYMENT TRACKING
// =============================================================================

/**
 * Track payment initiated (Mollie link clicked)
 */
export function trackPaymentInitiated(werkOrderId: string, amount: number) {
  trackEvent('payment_initiated', { werkOrderId, amount })
}

// =============================================================================
// TIMELINE TRACKING
// =============================================================================

/**
 * Track timeline step viewed (user clicks completed step)
 */
export function trackTimelineStepViewed(werkOrderId: string, step: string) {
  trackEvent('timeline_step_viewed', { werkOrderId, timelineStep: step })
}

// =============================================================================
// TERUGKOM-MOMENT + KLEED-ABONNEMENT (plan 2026-10-22)
// =============================================================================

// Server-side telt client_app_events hetzelfde (plan 2026-10-26); GTM blijft
// voor de funnel in GA4. Eventnamen ongewijzigd zodat de meting doorloopt.

export function trackTerugkomViewed(werkOrderId: string, optieCount: number, bron?: string) {
  trackEvent('terugkom_viewed', { werkOrderId, optieCount, bron })
}

export function trackTerugkomConfirmed(werkOrderId: string, seizoen: string, kanaal: string, bron?: string) {
  trackEvent('terugkom_confirmed', { werkOrderId, seizoen, kanaal, bron })
}

export function trackTerugkomDeclined(werkOrderId: string, bron?: string) {
  trackEvent('terugkom_declined', { werkOrderId, bron })
}

export function trackAbonnementViewed(werkOrderId: string, bron?: string) {
  trackEvent('abonnement_viewed', { werkOrderId, bron })
}

export function trackAbonnementConfirmed(werkOrderId: string, intervalMaanden: number, seizoen: string, bron?: string) {
  trackEvent('abonnement_confirmed', { werkOrderId, intervalMaanden, seizoen, bron })
}

// =============================================================================
// MIJN KLEDA (plan 2026-10-26)
// =============================================================================

export function trackMijnKledaViewed(werkOrderId: string, niveau?: 'basis' | 'volledig') {
  trackEvent('mijn_kleda_viewed', { werkOrderId, niveau })
}

// Inloggen op Mijn Kleda (plan 2026-10-28, golf 1). Geen e-mailadres in GTM.
export function trackMijnKledaInlogAangevraagd(bron: string) {
  trackEvent('mijn_kleda_inlog_aangevraagd', { bron })
}

export function trackMijnKledaIngelogd() {
  trackEvent('mijn_kleda_ingelogd', {})
}

export function trackMijnKledaUitgelogd(werkOrderId: string) {
  trackEvent('mijn_kleda_uitgelogd', { werkOrderId })
}

export function trackBestelOpnieuwClicked(werkOrderId: string) {
  trackEvent('bestel_opnieuw_clicked', { werkOrderId })
}

export function trackMailvoorkeurChanged(werkOrderId: string, aan: boolean) {
  trackEvent('mailvoorkeur_changed', { werkOrderId, aan })
}

// Gegevens wijzigen op Mijn Kleda (plan 2026-10-28, golf 2). Geen adres, nummer of e-mail in GTM.
export function trackGegevensGewijzigd(werkOrderId: string, veld: 'adres' | 'telefoon' | 'email', scope?: string) {
  trackEvent('gegevens_gewijzigd', { werkOrderId, veld, scope })
}

export function trackEmailBevestigd() {
  trackEvent('email_bevestigd', {})
}
